import {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from "react";
import {
  getErrorMessage,
  isResponseError,
  postMediaEndlink,
  postMultipleMediaEndlinks
} from "@coxauto-ui/cxm/communications/api";
import {
  EndlinkMediaMap,
  MediaItem
} from "@coxauto-ui/cxm/communications/interfaces";
import { useCxmFlags } from "@coxauto-ui/core/third-party/launch-darkly";
import { ConversationContext } from "../../context";

export interface MediaEndlinkInsert {
  endlinkUrl: string;
  media: MediaItem[];
  // Server-issued tracking id for this endlink. Required by the text channel's
  // pending-endlink chip and the future per-iteration sendCommunication
  // mediaInfo payload. May be null when the BFF response omits it.
  mediaViewTrackingId: string | null;
}

export interface UseMediaLibraryPickerParams {
  selectedMedia: MediaItem[];
  setSelectedMedia: Dispatch<SetStateAction<MediaItem[]>>;
  // Email-only: text channel uses `pendingEndlinks` instead of the URL→media map.
  setEndlinkMediaMap?: Dispatch<SetStateAction<EndlinkMediaMap>>;
  onInsertContent: (items: MediaEndlinkInsert[]) => void;
  onComplete: () => void;
}

export interface UseMediaLibraryPickerResult {
  selectedMedia: MediaItem[];
  isInserting: boolean;
  handleMediaSelect: (newPicks: MediaItem[]) => Promise<void>;
  endlinkError: string | null;
  clearEndlinkError: () => void;
}

export function useMediaLibraryPicker({
  selectedMedia,
  setSelectedMedia,
  setEndlinkMediaMap,
  onInsertContent,
  onComplete
}: UseMediaLibraryPickerParams): UseMediaLibraryPickerResult {
  const { commonOrgId, customerId, customerSystem, bridgeUserId, env } =
    useContext(ConversationContext);
  const { "nx.cxm.inbox.use-single-endlink": useSingleEndlink } = useCxmFlags();
  const [endlinkError, setEndlinkError] = useState<string | null>(null);
  const [isInserting, setIsInserting] = useState(false);

  // Clear media state when the endlink strategy flag changes mid-session
  // to prevent stale map entries from the previous strategy.
  // TODO: Remove this useEffect when the nx.cxm.inbox.use-single-endlink flag is removed.
  const singleEndlinkRef = useRef(useSingleEndlink);
  useEffect(() => {
    if (singleEndlinkRef.current !== useSingleEndlink) {
      singleEndlinkRef.current = useSingleEndlink;
      setSelectedMedia([]);
      setEndlinkMediaMap?.({});
    }
  }, [useSingleEndlink, setSelectedMedia, setEndlinkMediaMap]);

  const requestBody = useCallback(
    (items: MediaItem[]) => ({
      coOrgId: commonOrgId,
      consumerId: customerId,
      consumerRecordSystem: customerSystem,
      bridgeUserId,
      mediaItems: items.map(m => ({
        id: m.id,
        thumbnailUrl: m.thumbnailUrl ?? null,
        previewUrl: m.previewUrl ?? null
      }))
    }),
    [commonOrgId, customerId, customerSystem, bridgeUserId]
  );

  const handleMediaSelect = useCallback(
    async (newPicks: MediaItem[]) => {
      setEndlinkError(null);
      setIsInserting(true);

      if (useSingleEndlink) {
        // Flag ON: single endlink for all media — one widget with multiple thumbnails.
        // Full replace (not accumulate) since all items share one endlink.
        setSelectedMedia(newPicks);

        try {
          const result = await postMediaEndlink(requestBody(newPicks), env);

          if (isResponseError(result)) {
            setSelectedMedia([]);
            setEndlinkError(getErrorMessage(result));
            return;
          }

          const trackingId = result.mediaViewTrackingId;
          setEndlinkMediaMap?.({
            [result.endlinkUrl]: {
              mediaIds: newPicks.map(m => m.id),
              trackingIds: trackingId ? [trackingId] : [],
              titles: newPicks.map(m => m.title ?? "")
            }
          });
          onInsertContent([
            {
              endlinkUrl: result.endlinkUrl,
              media: newPicks,
              mediaViewTrackingId: trackingId ?? null
            }
          ]);
        } catch {
          setSelectedMedia([]);
          setEndlinkError("An unexpected error occurred. Please try again.");
        } finally {
          setIsInserting(false);
          onComplete();
        }
      } else {
        // Flag OFF (default): one endlink per media item — individual widgets.
        // Accumulate media across selections.
        setSelectedMedia(prev => [...prev, ...newPicks]);

        try {
          const result = await postMultipleMediaEndlinks(
            requestBody(newPicks),
            env
          );

          if (isResponseError(result)) {
            setSelectedMedia(prev =>
              prev.filter(m => !newPicks.some(np => np.id === m.id))
            );
            setEndlinkError(getErrorMessage(result));
            return;
          }

          // Populate endlink → media/tracking map for email send-time reconciliation.
          setEndlinkMediaMap?.(prev => {
            const additions: EndlinkMediaMap = {};
            for (const item of result.mediaItems) {
              const originalPick = newPicks.find(m => m.id === item.id);
              additions[item.endlinkUrl] = {
                mediaIds: [item.id],
                trackingIds: item.mediaViewTrackingId
                  ? [item.mediaViewTrackingId]
                  : [],
                titles: [originalPick?.title ?? ""]
              };
            }
            return { ...prev, ...additions };
          });

          const inserts: MediaEndlinkInsert[] = result.mediaItems.map(item => {
            const originalPick = newPicks.find(m => m.id === item.id);
            return {
              endlinkUrl: item.endlinkUrl,
              media: [
                {
                  id: item.id,
                  thumbnailUrl: item.thumbnailUrl ?? originalPick?.thumbnailUrl,
                  previewUrl: item.previewUrl ?? originalPick?.previewUrl,
                  title: originalPick?.title
                }
              ],
              mediaViewTrackingId: item.mediaViewTrackingId ?? null
            };
          });
          onInsertContent(inserts);
        } catch {
          setSelectedMedia(prev =>
            prev.filter(m => !newPicks.some(np => np.id === m.id))
          );
          setEndlinkError("An unexpected error occurred. Please try again.");
        } finally {
          setIsInserting(false);
          onComplete();
        }
      }
    },
    [
      useSingleEndlink,
      setSelectedMedia,
      setEndlinkMediaMap,
      requestBody,
      env,
      onInsertContent,
      onComplete
    ]
  );

  const clearEndlinkError = useCallback(() => setEndlinkError(null), []);

  return {
    selectedMedia,
    isInserting,
    handleMediaSelect,
    endlinkError,
    clearEndlinkError
  };
}
